"use client";

import NumberFlow from "@number-flow/react";
import { motion } from "framer-motion";
import { ArrowDown, ChevronDown, CreditCard } from "lucide-react";
import Link from "next/link";
import { useMemo, useState } from "react";
import { AssetIcon } from "@/components/ui/asset-icon";
import { GlassCard } from "@/components/ui/glass-card";
import { Button } from "@/components/ui/button";
import { useLivePrices } from "@/components/providers/prices-provider";
import { useAnalytics } from "@/hooks/use-analytics";
import { cn } from "@/lib/utils";
import { formatUsd } from "@/lib/format";
import { CHAINS, CRYPTO, STOCKS, type Asset, type Chain } from "@/lib/landing-data";
import { APP_URL } from "@/lib/links";

export type TradeMode = "buy" | "sell" | "convert";

export interface TradeState {
  mode: TradeMode;
  payAsset: Asset;
  receiveAsset: Asset;
  chain: Chain;
  /** Amount of `payAsset` the user is paying. */
  payAmount: number;
}

export const FIAT: Asset = { symbol: "USD", name: "US Dollar", color: "#16a34a", kind: "stable" };

const TABS: { id: TradeMode; label: string }[] = [
  { id: "buy", label: "Buy" },
  { id: "sell", label: "Sell" },
  { id: "convert", label: "Convert" },
];

// Pools the asset chips cycle through, per side.
const BUYABLE = [CRYPTO.SOL, CRYPTO.BTC, CRYPTO.ETH, STOCKS.NVDA, STOCKS.TSLA, CRYPTO.BONK];
const SELLABLE = [CRYPTO.SOL, CRYPTO.ETH, CRYPTO.BTC, CRYPTO.JUP];
const CONVERT_TO = [CRYPTO.ETH, CRYPTO.SOL, CRYPTO.BTC, STOCKS.AAPL];

/** Parse a curated "$1,234.56" display string into a number (fallback only). */
function parseCuratedPrice(asset: Asset): number {
  if (asset.symbol === "USD") return 1;
  const n = Number((asset.price ?? "$0").replace(/[^0-9.]/g, ""));
  return n || 1;
}

function defaultState(mode: TradeMode): TradeState {
  if (mode === "sell") {
    return { mode, payAsset: CRYPTO.SOL, receiveAsset: FIAT, chain: CHAINS[1], payAmount: 5 };
  }
  if (mode === "convert") {
    return { mode, payAsset: CRYPTO.SOL, receiveAsset: CRYPTO.ETH, chain: CHAINS[0], payAmount: 10 };
  }
  return { mode, payAsset: FIAT, receiveAsset: CRYPTO.SOL, chain: CHAINS[1], payAmount: 100 };
}

function quickAmounts(mode: TradeMode): number[] {
  return mode === "sell" ? [1, 5, 25] : mode === "convert" ? [1, 10, 50] : [50, 100, 500];
}

function ctaLabel(mode: TradeMode, receive: Asset): string {
  if (mode === "sell") return "Cash out";
  if (mode === "convert") return `Convert to ${receive.symbol}`;
  return `Buy ${receive.symbol}`;
}

interface DemoTradeWidgetProps {
  className?: string;
  initialMode?: TradeMode;
  /** When provided, the widget is fully controlled (for scroll-driven scripting). */
  state?: TradeState;
  /** Disable pointer interaction (use with `state` for scripted set-pieces). */
  interactive?: boolean;
}

/**
 * The rebrand trade widget, faithfully recreated for the web landing page:
 * pill Buy/Sell/Convert tabs, you-pay / you-receive fields, a chain selector,
 * glow CTA. Fully interactive by default; pass `state` + `interactive={false}`
 * to drive it from scroll in a set-piece.
 */
export function DemoTradeWidget({
  className,
  initialMode = "buy",
  state: controlled,
  interactive = true,
}: DemoTradeWidgetProps) {
  const [internal, setInternal] = useState<TradeState>(() => defaultState(initialMode));
  const state = controlled ?? internal;
  const { mode, payAsset, receiveAsset, chain, payAmount } = state;
  const analytics = useAnalytics();
  const live = useLivePrices();

  // Live price when available (crypto/stables), else the curated fallback.
  const priceOf = (asset: Asset): number =>
    asset.symbol === "USD" ? 1 : live[asset.symbol]?.price ?? parseCuratedPrice(asset);

  // The non-fiat side, used for the "1 X = $…" rate line.
  const quoteAsset = receiveAsset.symbol === "USD" ? payAsset : receiveAsset;
  const quoteLive = live[quoteAsset.symbol]?.price;
  const quotePriceLabel = quoteLive != null ? formatUsd(quoteLive) : quoteAsset.price;

  const update = (patch: Partial<TradeState>) => {
    if (controlled) return;
    setInternal((s) => ({ ...s, ...patch }));
  };

  const receiveAmount = useMemo(() => {
    const rate = priceOf(payAsset) / priceOf(receiveAsset);
    return payAmount * rate;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payAsset, receiveAsset, payAmount, live]);

  const isFiatReceive = receiveAsset.symbol === "USD";
  const showChain = mode !== "sell";

  const setMode = (next: TradeMode) => update(defaultState(next));

  const cycle = (side: "pay" | "receive") => {
    const pool = mode === "sell" ? SELLABLE : mode === "convert" ? CONVERT_TO : BUYABLE;
    const current = side === "pay" ? payAsset : receiveAsset;
    if (mode === "buy" && side === "pay") return; // pay side is fiat for buy
    if (mode === "sell" && side === "receive") return; // receive is fiat for sell
    const idx = pool.findIndex((a) => a.symbol === current.symbol);
    const nextAsset = pool[(idx + 1 + pool.length) % pool.length];
    update(side === "pay" ? { payAsset: nextAsset } : { receiveAsset: nextAsset });
  };

  const cycleChain = () => {
    const idx = CHAINS.findIndex((c) => c.id === chain.id);
    update({ chain: CHAINS[(idx + 1) % CHAINS.length] });
  };

  return (
    <GlassCard glow className={cn("w-full max-w-sm p-4 sm:p-5", !interactive && "pointer-events-none", className)}>
      {/* Pill tabs */}
      <div className="flex items-center gap-1 rounded-full border border-border/70 bg-muted/40 p-1">
        {TABS.map((t) => {
          const active = t.id === mode;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setMode(t.id)}
              className={cn(
                "relative flex-1 rounded-full px-3 py-1.5 text-sm font-medium transition-colors",
                active ? "text-primary-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {active && (
                <motion.span
                  layoutId="trade-tab"
                  transition={{ type: "spring", stiffness: 380, damping: 32 }}
                  className="absolute inset-0 -z-10 rounded-full bg-primary shadow-glow"
                />
              )}
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Fields */}
      <div className="relative mt-3 space-y-1.5">
        <Field
          label="You pay"
          asset={payAsset}
          amount={payAmount}
          isFiat={payAsset.symbol === "USD"}
          onAsset={() => cycle("pay")}
          locked={mode === "buy"}
        />

        <div className="relative flex h-0 items-center justify-center">
          <span className="absolute z-10 grid size-9 place-items-center rounded-full border border-border bg-card text-primary shadow-sm">
            <ArrowDown className="size-4" />
          </span>
        </div>

        <Field
          label="You receive"
          asset={receiveAsset}
          amount={receiveAmount}
          isFiat={isFiatReceive}
          muted
          onAsset={() => cycle("receive")}
          locked={mode === "sell"}
        />
      </div>

      {/* Chain selector */}
      {showChain && (
        <button
          type="button"
          onClick={cycleChain}
          className="mt-3 flex w-full items-center justify-between rounded-xl border border-border/60 bg-background/40 px-3 py-2 text-sm transition-colors hover:border-primary/40"
        >
          <span className="text-muted-foreground">Receive on</span>
          <span className="flex items-center gap-2 font-medium">
            <AssetIcon asset={{ symbol: chain.name, logo: chain.logo, color: chain.color }} size={18} />
            {chain.name}
            <ChevronDown className="size-4 text-muted-foreground" />
          </span>
        </button>
      )}

      {/* Quick amounts */}
      {interactive && (
        <div className="mt-3 flex gap-2">
          {quickAmounts(mode).map((amt) => (
            <button
              key={amt}
              type="button"
              onClick={() => update({ payAmount: amt })}
              className={cn(
                "flex-1 rounded-lg border px-2 py-1.5 text-xs font-medium transition-colors",
                payAmount === amt
                  ? "border-primary/60 bg-primary/10 text-foreground"
                  : "border-border/60 text-muted-foreground hover:text-foreground",
              )}
            >
              {payAsset.symbol === "USD" ? `$${amt}` : `${amt} ${payAsset.symbol}`}
            </button>
          ))}
        </div>
      )}

      {/* Rate */}
      <p className="mt-3 text-center text-xs text-muted-foreground">
        1 {quoteAsset.symbol} = {quotePriceLabel} · fees included
      </p>

      <Button asChild variant="gradient" size="lg" className="mt-3 w-full">
        <Link
          href={APP_URL}
          onClick={() =>
            analytics.trackButton(ctaLabel(mode, receiveAsset), "Trade widget", {
              mode,
              button_url: APP_URL,
            })
          }
        >
          {ctaLabel(mode, receiveAsset)}
        </Link>
      </Button>

      <p className="mt-3 text-center text-[11px] text-muted-foreground">
        Any chain · no seed phrases · settles in seconds
      </p>
    </GlassCard>
  );
}

interface FieldProps {
  label: string;
  asset: Asset;
  amount: number;
  isFiat: boolean;
  muted?: boolean;
  locked?: boolean;
  onAsset: () => void;
}

function Field({ label, asset, amount, isFiat, muted, locked, onAsset }: FieldProps) {
  return (
    <div className="rounded-xl border border-border/60 bg-background/40 p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="mt-1 flex items-center justify-between gap-2">
        <span className={cn("text-2xl font-semibold tabular-nums", muted && "text-muted-foreground")}>
          <NumberFlow
            value={amount}
            format={
              isFiat
                ? { style: "currency", currency: "USD", maximumFractionDigits: amount < 100 ? 2 : 0 }
                : { maximumFractionDigits: amount < 1 ? 6 : amount < 100 ? 4 : 2 }
            }
          />
          {!isFiat && <span className="ml-1 text-base text-muted-foreground">{asset.symbol}</span>}
        </span>

        <button
          type="button"
          onClick={onAsset}
          disabled={locked}
          className={cn(
            "flex items-center gap-2 rounded-full border border-border bg-card px-2.5 py-1.5 text-sm font-medium transition-colors",
            !locked && "hover:border-primary/40",
          )}
        >
          {isFiat ? (
            <span className="grid size-6 place-items-center rounded-full bg-success/15 text-success">
              <CreditCard className="size-3.5" />
            </span>
          ) : (
            <AssetIcon asset={asset} size={24} />
          )}
          {asset.symbol}
          {!locked && <ChevronDown className="size-4 text-muted-foreground" />}
        </button>
      </div>
    </div>
  );
}
